/** @module csi/datasheet/SaveCanvasController */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/Deferred",
	"dojo/promise/all",
	"csi/_base/underscore",
	"csi/action/Controller",
	"csi/api",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/locale",
	"csi/monitor/_base",
	"csi/session",
	"csi/state",
	"csi/state/rootStore",
	"csi/widget/_svg/util",
	"csi/widget/MessageDialog",
	"csi/xml"
], function (declare, lang, Deferred, all, _, Controller, api, comm, PostRequest, locale, monitor, session, state, { sessionStore }, svgUtil, MessageDialog, xml) {

	var PDF_SERVICE_FAIL = "CanvasSnapshotError";
	var CANVAS_SNAPSHOT_UNAVAILABLE = "CanvasSnapshotUnavailable";
	var IMAGE_SIZE_LIMIT = 1048576;	// 1MB (binary)

	/**
	 * Delete urls in old list that are not in new list
	 */
	function _deleteDifference(writer, oldUrls, newUrls) {
		_.each(_.difference(oldUrls, newUrls), function (url) {
			writer.deleteNode(url);
		});
	}

	return declare(Controller, {
		/** @constant {number} PDF_TIMEOUT - maximum time to wait for PDFRequest to process before continuing with Save */
		PDF_TIMEOUT: 60000,

		/** @property {boolean} bFinish - set to true to exit edit mode after saving */
		bFinish: true,

		/** @property {boolean} bPdfFailed - if PDF service fails, bPdfFailed is true  */
		bPdfFailed: false,

		/** @property {boolean} bRegenerateSnapshots - indicate that regenerating snapshots is only purpose of save */
		bRegenerateSnapshots: false,

		/** @property {boolean} bReviewUpdates - if we're reviewing updates, this is true  */
		bReviewUpdates: false,

		/**
		 * Overriding the preProcess method here from Controller.js, this is hook before the process get called.
		 * Generating svg for the pages those do not have svg, this will make sure all the pages in svgStore have svg before reaching to process method.
		 * For non-chrome browser simply calling the super method.
		 */
		preProcess: async function () {
			// show dialog
			$('body').addClass("csi-canvas-progress");

			this.displayProgressDialog({
				message: this.getString(this.bRegenerateSnapshots ? "_Regenerating" : "_Progress"),
				progressValue: 0,
				onDestroy: function () {
					$('body').removeClass("csi-canvas-progress");
				}
			});

			this.process();
		},

		/**
		 * Take control of process function.
		 * We want to convert SVG to Base64, this operation is asynchronous and
		 * we want to publish changes once all pages are processed.
		 * OnAction update Images attribute with those used on canvas - make sure to handle usual case of
		 * DataSheet.Images being an array, but DataSheet could also be an ImageContainer in which
		 * case Images will be a map.
		 */
		process: function () {
			var self = this;
			var view = this.widget;
			var cache = this.m_cache;
			var writer = this.m_writer;
			var urlMap = view.mapSavedUrls;
			var pageUrls = view.getVisibleNodes();
			var layout = view.getCanvasPageLayoutMinusTitleblock();

			// if for some page ThumbnailGenerated is set to false or missing Snapshots, we need to try save again even if canvas is not dirty.
			var thumbnailPending = _.some(pageUrls, function (url) {
				var pageNode = cache.getNode(url);
				return !pageNode.ThumbnailGenerated || !xml.isValidURL(pageNode.Snapshot);
			});

			// if there are no changes to canvas or all thumbnails are generated then
			// publish the changes and return.
			if (!view.isDirty() && !thumbnailPending) {
				self._onPublishResponse();
				return;
			}

			// get new list of pages, creating nodes along the way for new pages that have never been saved before
			var newPageUrls = _.map(pageUrls, function (url) {
				var urlTemp = url;

				if (xml.isClientUrl(url)) {
					if (urlMap[url]) {
						url = urlMap[url];
					} else {
						url = comm.createUrl();
						writer.createNode(url, "DataSheetPage");
					}
				}

				// if url was deleted, but then changes were undone, then recreate the node
				if (cache.isDeleted(url)) {
					writer.createNode(url, "DataSheetPage");
				}

				// build mapping both ways for pages to be used later
				urlMap[urlTemp] = url;
				urlMap[url] = urlTemp;
				return url;
			}, this);

			var oldPageUrls = this.getParent()[this.m_idAttrParent];

			if (!_.isEqual(newPageUrls, oldPageUrls)) {
				// delete pages
				_deleteDifference(writer, oldPageUrls, newPageUrls);

				// change pages if new or deleted pages, or if order changed
				writer.changeNode(this.m_urlParent).changeAttribute(this.m_idAttrParent, xml.ATTR_REFVECTOR, newPageUrls);
			}

			var startDfd = new Deferred();
			var overallDfd = startDfd;
			var monitorDfds = [];
			var pageRequestArgs = [];
			var nProgress = 0;
			var nTotal = 0;
			var pdfPollingInterval = 1000;
			var pdfMaxDelay = this.PDF_TIMEOUT;

			function monitorPage(response) {
				cache.cacheResponse(response);

				// if canvas page is new, then a client node will be used to track its progress;
				// need to make sure to keep client node in sync with server node
				var serverPage = response.getNodeOfType("DataSheetPage");
				var clientPage = cache.getNode(urlMap[serverPage.$URL]);

				clientPage.ThumbnailGenerated = serverPage.ThumbnailGenerated;

				var pdfRequest = response.getNodeOfType("PDFRequest");
				if (pdfRequest) {
					// wait for PDFRequest to process - 1 second polling interval only used if websockets are not used
					var dfd = monitor.waitForNodeCondition(pdfRequest, function (node) {
						if (node.Status === "Succeeded") {
							// if page is new, then this will keep clientPage in sync with serverPage
							clientPage.ThumbnailGenerated = serverPage.ThumbnailGenerated = true;
							return true;
						}
						self.bPdfFailed = (node.Status == "Failed");
						return !node || self.bPdfFailed;
					}, pdfPollingInterval, pdfMaxDelay);

					// whether it succeeds or fails, report progress
					dfd = dfd.then(onProgress, onProgress);

					monitorDfds.push(dfd);
				} else {
					onProgress();
				}
			}

			function onDone() {
				self._onPublishResponse();
			}

			function onProgress() {
				self.displayProgressValue(++nProgress / nTotal);
			}

			function onProgressWithResponse(response) {
				if (response) {
					cache.cacheResponse(response);
				}

				onProgress();
			}

			function saveCanvas() {
				return writer.execute();
			}

			function savePage(ioArgs) {
				return new PostRequest().post(ioArgs);
			}

			function savePages() {
				// save each page one by one, and then start monitoring for its snapshot to be complete
				var firstDfd = new Deferred();
				var pagesDfd = firstDfd;

				_.each(pageRequestArgs, function (ioArgs) {
					pagesDfd = pagesDfd
						.then(lang.partial(savePage, ioArgs))
						.then(monitorPage);
				});

				pagesDfd = pagesDfd
					.then(updateImages)
					.then(onProgressWithResponse)
					.then(waitForPdfRequests);

				firstDfd.resolve();

				return pagesDfd;
			}

			function updateImages() {
				// after all pages are processed update images if canvas is for a DataSheet
				var request;

				if (view.isDataSheetCanvas()) {
					request = new PostRequest("SiteAdmin", "ResetDataSheetImages").addParameter(xml.PROP_URL, self.getParent().__Parent__);
				} else {
					request = new PostRequest("SiteAdmin", "ResetCanvasContainerImages").addParameter(xml.PROP_URL, self.m_urlParent);
				}

				return request.post();
			}

			function waitForPdfRequests() {
				return all(monitorDfds);
			}

			_.each(pageUrls, function (url) {
				const urlNew = urlMap[url];
				const content = view.svgStore.get(url);
				const oldPage = cache.getNode(urlNew);
				const thumbnailGenerated = oldPage && oldPage.ThumbnailGenerated;
				const snapshotGenerated = oldPage && xml.isValidURL(oldPage.Snapshot);

				// handle changes to notes
				const oldNoteUrls = oldPage ? oldPage.Comments : [];
				const newNoteUrls = _.map(content.notes, function (url) {
					const note = cache.getNode(url);
					let cn;
					if (urlMap[url]) {
						url = urlMap[url];
						cn = writer.changeNode(url);
					} else if (xml.isClientUrl(url)) {
						url = comm.createUrl();
						cn = writer.createNode(url, "Comment");
					}

					if (cn) {
						cn.changeAttribute("Author", xml.ATTR_REF, note.Author);
						cn.changeAttribute("Comment", xml.ATTR_STRING, note.Comment);
						cn.changeAttribute("Date", xml.ATTR_TIME, note.Date);
					}

					urlMap[url] = url;
					return url;
				});

				if (!_.isEqual(newNoteUrls, oldNoteUrls)) {
					// delete old comments no longer referenced
					_deleteDifference(writer, oldNoteUrls, newNoteUrls);

					// update list of comments on page
					writer.changeNode(urlNew).changeAttribute("Comments", xml.ATTR_REFVECTOR, newNoteUrls);
				}

				if (!content.data) {
					return;
				}

				var pageModel = JSON.parse(content.data);
				pageModel.id = urlNew;

				// previous bugs have allowed bad data, fix this bad data by making it valid before saving each page
				pageModel.pageObjects = view.canvasWidgetNode.canvasApp.pageHelper.getValidPageObjects(pageModel.pageObjects);

				var contentKeys = view.canvasWidgetNode.getCanvasContentKeys(pageModel);
				var contentJson = JSON.stringify(pageModel);

				// handle changes to SVG content
				if (!oldPage || !thumbnailGenerated || !snapshotGenerated || (content.split != oldPage.SplitPct) || !svgUtil.compareJSONContents(contentJson, oldPage.ContentJson)) {
					var height = layout.h * (layout.vertical ? content.split : 1);
					var width = layout.w * (layout.vertical ? 1 : content.split);
					const MAX_DIM = 1600 * api.getSnapshotQualityFactor();
					var scale = MAX_DIM / Math.max(height, width);
					var scaledWidth = Math.round(width * scale);
					var scaledHeight = Math.round(height * scale);
					const emptyCanvas = !svgUtil.isNonEmptyPage(pageModel) || !scaledHeight || !scaledWidth;

					function pageToBlob() {
						// try to generate snapshot blob for HTML/SVG content if possible
						return svgUtil.svgToBlob(content.svg, scale, scaledHeight, scaledWidth, IMAGE_SIZE_LIMIT);
					}

					function blobToRequest(blob) {
						// TODO: Convert to PostRequest
						const data = new FormData();
						data.append("Module", "SiteAdmin");
						data.append("Operation", "GenerateCanvasSnapshot");
						data.append("URL", urlNew);
						data.append("Width", scaledWidth);
						data.append("Height", scaledHeight);
						data.append("OutputJSON", "2");

						if (blob) {
							const { appVersion } = navigator;
							data.append("BrowserVersion", appVersion);
							data.append("Snapshot", blob);
						}

						pageRequestArgs.push({
							headers: {},
							data,
							handleAs: "json",
						});
					}

					writer.callMethod("SiteAdmin", "SaveCanvas")
						.addParameter("URL", xml.ATTR_CNL, urlNew)
						.addParameter("ContentJson", xml.ATTR_STRING, contentJson)
						.addParameter("EmptyPage", xml.ATTR_BOOLEAN, emptyCanvas)
						.addParameter("Height", xml.ATTR_INTEGER, scaledHeight)
						.addParameter("Keys", xml.ATTR_STRINGMAP, contentKeys)
						.addParameter("Scale", xml.ATTR_DOUBLE, scale)
						.addParameter("SplitPct", xml.ATTR_DOUBLE, content.split)
						.addParameter("Width", xml.ATTR_INTEGER, scaledWidth);

					if (!emptyCanvas) {
						// add 2 to total number of progress steps, 1 for creating blob, and one for waiting for PDFRequest
						nTotal += 2;

						overallDfd = overallDfd
							.then(pageToBlob)
							.then(blobToRequest)
							.then(onProgressWithResponse);
					}
				} else if (view.bFromMobile) {
					// handle the case where the page was created by the mobile apps, if not, then it'll be part of the operation above
					writer.callMethod("SiteAdmin", "ClientTakeOwnershipOfCanvas").addParameter("URL", xml.ATTR_REF, urlNew);
				}
			}, this);

			// publish changes to create/change pages and notes
			if (writer.isChanged()) {
				++nTotal;

				overallDfd = overallDfd
					.then(saveCanvas)
					.then(onProgressWithResponse);
			}

			++nTotal;
			overallDfd = overallDfd.then(savePages);

			self.when(overallDfd, onDone);

			// start processing now that deferred chain is set
			startDfd.resolve();
		},

		/**
		 * Check for any problems generating canvas snapshots
		 * @param {Response} response
		 */
		_onPublishResponse: function (response) {
			this.displayProgressValue(1.0);

			var pageContainer = this.getParent();
			var pages = this.m_cache.getNodes(pageContainer.Pages);
			var bError = _.some(pages, function (page) { return !page.ThumbnailGenerated; });
			if (this.bPdfFailed) {
				this.displayErrorDialog({
					message: locale.resource(PDF_SERVICE_FAIL),
					onResponse: lang.hitch(this, Controller.prototype._onPublishResponse, response)
				});
			} else if (bError) {
				// at least 1 canvas snapshot is missing
				this.displayErrorDialog({
					title: locale.resource("WARNING"),
					materialIcon: MessageDialog.ICON_WARNING,
					message: locale.resource(CANVAS_SNAPSHOT_UNAVAILABLE),
					onResponse: lang.hitch(this, Controller.prototype._onPublishResponse, response)
				});
			} else {
				// request pages with updated snapshots before proceeding further
				// TODO: could optimize this  by only requests pages that were modified
				const request = comm.getNodeRequest(this.m_urlNode).addDependencyPaths([xml.path("Pages", "Snapshot")]);
				this.when(this.m_cache.addRequestDeferred(request), (response) => {
					Controller.prototype._onPublishResponse.call(this, response);
				});
			}
		},

		/**
		 * Redraw view after save with new data stores
		 */
		postProcessSuccess: function () {
			// Reset in case user just saved after accepting or ignoring placeholder changes.
			this.widget.canvasWidgetNode.placeholdersModified = false;

			if (this.bFinish) {
				this.widget.exitEditMode(true);
			} else {
				this.widget.set("saved", true);
				state.set("modalView", null);
			}

			this.widget.refreshTemplatePages();
		},

		/**
		 * Check that user still has edit access to save the changes
		 * @return {Promise}
		 */
		_preAction: function () {
			// don't check for edit access if only regenerating snapshots
			if (this.bRegenerateSnapshots) {
				return true;
			}

			var self = this;
			var view = this.widget;
			var url = this.m_urlParent;
			var urlOwner = view.canvasOwner.$URL;

			return this.when(comm.getNodeRequest([url, urlOwner]).go(),
				function (response) {
					var node = response.getNode(url);
					if (node) {
						self.m_cache.put(node);
					} else {
						self.m_cache.remove(url);
					}

					if (view.canEditCanvas()) {
						return true;
					}

					if (node) {
						self.displayErrorDialog({
							suffix: "_ErrorFrozen",
							onResponse: function () {
								view.exitEditMode();
							}
						});
					} else {
						// possible cases here are:
						//	Theme is deleted (parent)
						//	DataSheetRevision is deleted using Purge Revisions
						//	DataSheet is deleted (master)
						//	Style is deleted (owner)
						self.displayErrorDialog({
							suffix: "_ErrorDelete",
							onResponse: function () {
								view.exitEditMode();
								session.setLocationUrl(response.getNode(urlOwner) ? urlOwner : sessionStore.homeUrl);
							}
						});
					}

					return false;
				}
			);
		}
	});
});
